export type Rect = {
  x: number;
  y: number;
  width: number;
  height: number;
  borderRadius?: number;
};

export type MaskRect = {
  x: number;
  y: number;
  width: number;
  height: number;
  cornerRadius?: number;
};

// Extract the largest possible rectangles from the grid
const extractMaximalRectangles = (
  grid: boolean[][],
  cellRects: (Set<number> | null)[][], // Track which original rects cover each cell
  xCoords: number[],
  yCoords: number[],
  originalRects: MaskRect[], // Original rectangles for corner radius lookup
): MaskRect[] => {
  const visited: boolean[][] = grid.map((row) => row.map(() => false));
  const rectangles: MaskRect[] = [];

  for (let i = 0; i < grid.length; i++) {
    for (let j = 0; j < grid[i].length; j++) {
      if (grid[i][j] && !visited[i][j]) {
        const rect = findLargestRectangle(
          grid,
          cellRects,
          visited,
          i,
          j,
          xCoords,
          yCoords,
          originalRects,
        );
        if (rect) rectangles.push(rect);
      }
    }
  }

  return rectangles;
};

// Find the largest rectangle starting from a cell
const findLargestRectangle = (
  grid: boolean[][],
  cellRects: (Set<number> | null)[][],
  visited: boolean[][],
  startRow: number,
  startCol: number,
  xCoords: number[],
  yCoords: number[],
  originalRects: MaskRect[],
): MaskRect | null => {
  // Find maximum width at this row
  let maxWidth = 0;
  for (
    let col = startCol;
    col < grid[startRow].length &&
    grid[startRow][col] &&
    !visited[startRow][col];
    col++
  ) {
    maxWidth++;
  }

  if (maxWidth === 0) return null;

  // Find maximum height with this width
  let maxHeight = 0;
  for (let row = startRow; row < grid.length; row++) {
    let canExtend = true;
    for (let col = startCol; col < startCol + maxWidth; col++) {
      if (col >= grid[row].length || !grid[row][col] || visited[row][col]) {
        canExtend = false;
        break;
      }
    }
    if (!canExtend) break;
    maxHeight++;
  }

  // Mark cells as visited
  for (let row = startRow; row < startRow + maxHeight; row++) {
    for (let col = startCol; col < startCol + maxWidth; col++) {
      visited[row][col] = true;
    }
  }

  // Determine corner radius by checking which original rects contribute to each corner
  const rectX = xCoords[startCol];
  const rectY = yCoords[startRow];
  const rectWidth = xCoords[startCol + maxWidth] - xCoords[startCol];
  const rectHeight = yCoords[startRow + maxHeight] - yCoords[startRow];
  const rectRight = rectX + rectWidth;
  const rectBottom = rectY + rectHeight;

  // Check corners: top-left, top-right, bottom-left, bottom-right
  // For each corner, find the maximum corner radius from contributing rects
  let cornerRadius: number | undefined = undefined;

  // Collect all rect indices that contribute to this combined rectangle
  const contributingRects = new Set<number>();
  for (let row = startRow; row < startRow + maxHeight; row++) {
    for (let col = startCol; col < startCol + maxWidth; col++) {
      const cellRectSet = cellRects[row]?.[col];
      if (cellRectSet) {
        cellRectSet.forEach((idx) => contributingRects.add(idx));
      }
    }
  }

  // Check if any contributing rect has a corner radius and if this combined rect
  // aligns with the corners of those rects
  const cornerRadiuses: number[] = [];
  contributingRects.forEach((rectIdx) => {
    const origRect = originalRects[rectIdx];
    if (origRect.cornerRadius && origRect.cornerRadius > 0) {
      // Check if this combined rect's corners align with the original rect's corners
      const origRight = origRect.x + origRect.width;
      const origBottom = origRect.y + origRect.height;

      // Top-left corner
      if (Math.abs(rectX - origRect.x) < 0.1 && Math.abs(rectY - origRect.y) < 0.1) {
        cornerRadiuses.push(origRect.cornerRadius);
      }
      // Top-right corner
      if (Math.abs(rectRight - origRight) < 0.1 && Math.abs(rectY - origRect.y) < 0.1) {
        cornerRadiuses.push(origRect.cornerRadius);
      }
      // Bottom-left corner
      if (Math.abs(rectX - origRect.x) < 0.1 && Math.abs(rectBottom - origBottom) < 0.1) {
        cornerRadiuses.push(origRect.cornerRadius);
      }
      // Bottom-right corner
      if (Math.abs(rectRight - origRight) < 0.1 && Math.abs(rectBottom - origBottom) < 0.1) {
        cornerRadiuses.push(origRect.cornerRadius);
      }
    }
  });

  // Use the maximum corner radius if any corners align
  if (cornerRadiuses.length > 0) {
    cornerRadius = Math.max(...cornerRadiuses);
  }

  return {
    x: rectX,
    y: rectY,
    width: rectWidth,
    height: rectHeight,
    cornerRadius,
  };
};

// Creates a grid of non-overlapping rects from a set of potentially overlapping rects. The result
// is a set of non-overlapping rectangles that cover the same area as the input rectangles.
export const createRectGrid = (rects: MaskRect[]): MaskRect[] => {
  if (rects.length <= 1) return rects;

  // 1. Collect all unique x and y coordinates
  const xCoords = new Set<number>();
  const yCoords = new Set<number>();

  rects.forEach((rect) => {
    xCoords.add(rect.x);
    xCoords.add(rect.x + rect.width);
    yCoords.add(rect.y);
    yCoords.add(rect.y + rect.height);
  });

  const sortedX = Array.from(xCoords).sort((a, b) => a - b);
  const sortedY = Array.from(yCoords).sort((a, b) => a - b);

  // 2. Create grid and mark covered cells, tracking which rects cover each cell
  const grid: boolean[][] = [];
  const cellRects: (Set<number> | null)[][] = [];
  for (let i = 0; i < sortedY.length - 1; i++) {
    grid[i] = [];
    cellRects[i] = [];
    for (let j = 0; j < sortedX.length - 1; j++) {
      const cellLeft = sortedX[j];
      const cellTop = sortedY[i];
      const cellRight = sortedX[j + 1];
      const cellBottom = sortedY[i + 1];

      // Check which rectangles cover this cell completely
      const coveringRects = new Set<number>();
      rects.forEach((rect, idx) => {
        if (
          cellLeft >= rect.x &&
          cellRight <= rect.x + rect.width &&
          cellTop >= rect.y &&
          cellBottom <= rect.y + rect.height
        ) {
          coveringRects.add(idx);
        }
      });

      grid[i][j] = coveringRects.size > 0;
      cellRects[i][j] = coveringRects.size > 0 ? coveringRects : null;
    }
  }

  // Step 3: Extract maximal rectangles from covered cells
  return extractMaximalRectangles(grid, cellRects, sortedX, sortedY, rects);
};

export const createClipPathData = (
  rects: MaskRect[],
  containerPosition: Rect,
  invert?: boolean,
) => {
  if (!containerPosition) return "";

  const { width, height } = containerPosition;

  // Start with full container rectangle (clockwise)
  let path = `M 0,0 L ${width},0 L ${width},${height} L 0,${height} Z`;
  if (invert) {
    // To invert, simply trace the container rect again to flip the evenodd rule
    path = ` M 0,0 L ${width},0 L ${width},${height} L 0,${height} Z`;
  }

  // Add each rect as a hole (counter-clockwise)
  rects.forEach((rect) => {
    const x = rect.x - containerPosition.x;
    const y = rect.y - containerPosition.y;
    const w = rect.width;
    const h = rect.height;
    const r = rect.cornerRadius || 0;

    if (r > 0) {
      const radius = Math.min(r, w / 2, h / 2);
      // M top-left → L bottom-left → L bottom-right → L top-right → Z
      path += ` M ${x + radius},${y}`; // Start at top-left + radius
      path += ` Q ${x},${y} ${x},${y + radius}`; // Top-left corner curve
      path += ` L ${x},${y + h - radius}`; // Down left edge
      path += ` Q ${x},${y + h} ${x + radius},${y + h}`; // Bottom-left corner
      path += ` L ${x + w - radius},${y + h}`; // Bottom edge (left to right)
      path += ` Q ${x + w},${y + h} ${x + w},${y + h - radius}`; // Bottom-right corner
      path += ` L ${x + w},${y + radius}`; // Up right edge
      path += ` Q ${x + w},${y} ${x + w - radius},${y}`; // Top-right corner
      path += ` L ${x + radius},${y}`; // Top edge (right to left)
      path += ` Z`;
    } else {
      // Counter-clockwise rectangle for hole
      path += ` M ${x},${y} L ${x},${y + h} L ${x + w},${y + h} L ${x + w
        },${y} Z`;
    }
  });

  return path;
};
